import { FC, useEffect, useRef, useState, Fragment } from 'react';
import styled from 'styled-components';
import { MoreVertical } from 'react-feather';
import { NavLink } from 'react-router-dom';
import Box from './primitives/Box';
import { useOutsideComponent } from '../../hooks';

interface Props {
  id: string;
  menus: {
    bg?: string;
    icon?: JSX.Element;
    label: string;
    hide?: boolean;
    to?: string;
    clickAction?: () => void;
  }[];
  popoverVisibility: any;
  setPopoverVisibility: any;
}

interface PopoverProps {
  popoverVisibility: boolean;
}

const Popover = styled('div')`
  position: relative;
  display: flex;
  width: 20px;
  z-index: 0;
`;

const PopoverInner = styled(Box as any)<PopoverProps>`
  box-shadow: 0 0 4px -1px rgba(50, 50, 93, 0.3);
  box-sizing: border-box;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  visibility: ${props => `${props.popoverVisibility}`};
  background: #ffffff;
  min-width: 300%;
  color: black;
  text-align: center;
  z-index: 0;
  top: -5px;
  left: -20px;
  padding: 5px 0px 0px 15px;
  position: relative;
`;

const ListItemLink = styled(NavLink as any)`
  list-style: none;
  padding-bottom: 0.5em;
  padding-top: 0.5em;
  padding-right: 0.5em;
  padding-left: 0.5em;
  &:hover {
    cursor: pointer;
    background: #e2e2e2;
  }
  color: black;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  text-decoration: none;
`;

const ListItem = styled('div')`
  list-style: none;
  padding-bottom: 0.5em;
  padding-top: 0.5em;
  padding-right: 0.5em;
  padding-left: 0.5em;
  &:hover {
    cursor: pointer;
    background: #e2e2e2;
  }
  color: black;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  text-decoration: none;
`;

const combiner = (originalObj: any, id: string) => {
  const copyObj = Object.assign({}, originalObj);
  Object.keys(originalObj).map(key => (copyObj[key] = key !== id ? 'hidden' : copyObj[key]));
  copyObj[id] = Boolean(!copyObj[id] || copyObj[id] === 'hidden') ? 'visible' : 'hidden';
  return copyObj;
};

const MoreVerticalMenu: FC<Props> = ({ id, menus, popoverVisibility, setPopoverVisibility }) => {
  const buttonRef = useRef(null);
  const wrapperRef = useRef(null);

  const [isVisible, setIsVisible] = useState(Boolean(popoverVisibility[id] && popoverVisibility[id] === 'visible'));

  const _handleToggleMenu = () => {
    if (isVisible) {
      const newObj = combiner(popoverVisibility, id);
      return setPopoverVisibility(newObj);
    }
  };

  useEffect(() => {
    setIsVisible(Boolean(popoverVisibility[id] === 'visible'));
  }, [popoverVisibility, id]);

  useOutsideComponent(wrapperRef, buttonRef, _handleToggleMenu, isVisible);

  return (
    <Fragment>
      <Popover>
        <div
          ref={buttonRef}
          onClick={() => {
            const newObj = combiner(popoverVisibility, id);
            return setPopoverVisibility(newObj);
          }}
        >
          <MoreVertical fontSize="0.3em" style={{ cursor: 'pointer' }} />
        </div>
      </Popover>
      <div style={{ position: 'sticky', ...(!isVisible && { display: 'none' }) }}>
        <PopoverInner popoverVisibility={popoverVisibility[id] || 'hidden'} ref={wrapperRef}>
          {menus.map((menu, index) => {
            if (menu.to && !menu.hide) {
              return (
                <ListItemLink key={index} to={menu.to}>
                  {menu.label}
                </ListItemLink>
              );
            }

            if (menu.clickAction && !menu.hide) {
              return (
                <ListItem key={index} onClick={menu.clickAction}>
                  {menu.label}
                </ListItem>
              );
            }

            return null;
          })}
        </PopoverInner>
      </div>
    </Fragment>
  );
};

export default MoreVerticalMenu;
